import React, { useMemo } from 'react'
import { Fade, Grid } from '@mui/material'
import { useQueryClient } from 'react-query'
import TablePanel from '../../Support/TablePanel'
import { EmptyPlaceholder } from '../../../shared/components/EmptyPlaceholder'
import { useLinkedRecordsExpansion } from '../../../shared/components/LinkedRecordsPanel'
import {
    DataTable,
    DataTableOpenPanelButton,
    DataTableSection,
    DataTableToolbar,
    getNoMatchesText,
    useDataTableFilters,
    useDataTableFiltersState,
    useNestedDataTableState,
} from '../../../shared/components/DataTable'
import { useIsRoadmapReadonly } from '../../../shared/hooks/useIsRoadmapReadonly'
import { useIsSnapshotMode } from '../../../shared/hooks/useIsSnapshotMode'
import { useStrategy } from '../../../shared/hooks/useStrategy'
import { useTableDetailPanel } from '../../../shared/hooks/useTableDetailPanel'
import useStrategicImperativeDisplayName from '../../../shared/hooks/useDisplayNames/variants/useStrategicImperativeDisplayName'
import {
    getStrategicImperativesQueryKey,
    StrategicImperative,
    useReorderStrategicImperativesMutation,
    useStrategicImperativesQuery,
} from '../../../shared/queries/strategicImperatives'
import { ActivityColumnId } from '../../Activities/ActivityTable/ActivityTable.types'
import LinkedActivitiesPanel from '../../Activities/ActivityTable/components/LinkedActivitiesPanel'
import { getStrategicImperativeColumns } from './StrategicImperativeTable.columns'
import {
    LINKED_ACTIVITIES_TABLE_SCOPE,
    LINKED_VALUE_MESSAGES_TABLE_SCOPE,
    STRATEGIC_IMPERATIVES_TABLE_SCOPE,
} from './StrategicImperativeTable.constants'
import {
    getStrategicImperativeFilterDefinitions,
    getStrategicImperativeSearchValues,
} from './StrategicImperativeTable.filters'
import { renderStrategicImperativeCell } from './components/renderStrategicImperativeCell'
import { StrategicImperativeColumnId, StrategicImperativeExpandedPanel } from './StrategicImperativeTable.types'
import { useStrategicImperativeDisplayNames } from './useStrategicImperativeDisplayNames'
import LinkedValueMessagesPanel from '../../ValueMessages/ValueMessageTable/components/LinkedValueMessagesPanel'
import { ValueMessageColumnId } from '../../ValueMessages/ValueMessageTable/ValueMessageTable.types'

const StrategicImperativeTable = () => {
    const { singular, plural } = useStrategicImperativeDisplayName()
    const displayNames = useStrategicImperativeDisplayNames()

    const queryClient = useQueryClient()
    const strategyId = useStrategy()?._id

    const isSnapshotMode = useIsSnapshotMode()
    const isRoadmapReadonly = useIsRoadmapReadonly()

    const strategicImperativesQuery = useStrategicImperativesQuery()
    const rows = useMemo(() => strategicImperativesQuery.data ?? [], [strategicImperativesQuery.data])

    const { mutate: reorderStrategicImperatives } = useReorderStrategicImperativesMutation()

    const filtersState = useDataTableFiltersState(STRATEGIC_IMPERATIVES_TABLE_SCOPE)
    const linkedActivitiesFiltersState = useDataTableFiltersState(LINKED_ACTIVITIES_TABLE_SCOPE)
    const linkedActivitiesTableState = useNestedDataTableState<ActivityColumnId>()
    const linkedValueMessagesFiltersState = useDataTableFiltersState(LINKED_VALUE_MESSAGES_TABLE_SCOPE)
    const linkedValueMessagesTableState = useNestedDataTableState<ValueMessageColumnId>()

    const linkedRecordsExpansion = useLinkedRecordsExpansion<StrategicImperativeExpandedPanel>(displayNames)

    const {
        openRow: openStrategicImperative,
        navigationStack,
        openPanel,
        closePanel,
    } = useTableDetailPanel(rows, 'Strategic Imperative')

    const { toolbarProps, hiddenColumnIds, hasActiveFilters, visibleRows } = useDataTableFilters({
        rows,
        filterDefinitions: getStrategicImperativeFilterDefinitions(displayNames),
        getSearchValues: getStrategicImperativeSearchValues,
        state: filtersState,
    })

    return (
        <Fade in timeout={800}>
            <Grid container item xs minWidth={0}>
                <DataTableSection
                    isLoading={strategicImperativesQuery.isLoading}
                    isError={strategicImperativesQuery.isError}
                    entityLabel={plural}
                    loadingTitle={plural}
                >
                    <DataTable<StrategicImperative, StrategicImperativeColumnId>
                        showIndexColumn
                        hasStickyColumns
                        columns={getStrategicImperativeColumns(displayNames)}
                        rows={visibleRows}
                        getRowId={(strategicImperative) => strategicImperative._id}
                        renderCell={renderStrategicImperativeCell(linkedRecordsExpansion)}
                        hiddenColumnIds={hiddenColumnIds}
                        renderRowActions={(strategicImperative) => (
                            <DataTableOpenPanelButton
                                accessibleLabel={`Open ${strategicImperative.name}`}
                                onClick={() => openPanel(strategicImperative._id)}
                            />
                        )}
                        expandedRowId={linkedRecordsExpansion.expandedRow?.id ?? null}
                        renderExpandedContent={(strategicImperative) =>
                            linkedRecordsExpansion.expandedRow?.panel === 'valueMessages' ? (
                                <LinkedValueMessagesPanel
                                    ownerName={strategicImperative.name}
                                    ownerLabel={singular}
                                    linkedIds={strategicImperative.linkedValueMessages}
                                    filtersState={linkedValueMessagesFiltersState}
                                    state={linkedValueMessagesTableState}
                                />
                            ) : (
                                <LinkedActivitiesPanel
                                    ownerName={strategicImperative.name}
                                    ownerLabel={singular}
                                    linkedIds={strategicImperative.linkedActivities}
                                    filtersState={linkedActivitiesFiltersState}
                                    state={linkedActivitiesTableState}
                                />
                            )
                        }
                        reorder={{
                            isDisabled: isSnapshotMode || isRoadmapReadonly || hasActiveFilters,
                            onReorder: reorderStrategicImperatives,
                        }}
                        toolbar={
                            <DataTableToolbar
                                {...toolbarProps}
                                searchPlaceholder={`${singular} Search`}
                                filterMenuWidth={260}
                            />
                        }
                        emptyState={
                            <EmptyPlaceholder
                                title={
                                    hasActiveFilters
                                        ? getNoMatchesText(plural.toLowerCase())
                                        : `No ${plural} to display`
                                }
                                subtitle={
                                    hasActiveFilters
                                        ? 'Clear the search or filters to see every row again.'
                                        : `Click 'Add New' on the left panel to create a new ${singular}`
                                }
                                sx={{ minHeight: 280 }}
                            />
                        }
                    />
                </DataTableSection>
                {!!openStrategicImperative && (
                    <TablePanel
                        data={openStrategicImperative}
                        type="strategicImperative"
                        closePanel={closePanel}
                        openPanelAction
                        updateParent={() => queryClient.invalidateQueries(getStrategicImperativesQueryKey(strategyId))}
                        navigationStack={navigationStack}
                    />
                )}
            </Grid>
        </Fade>
    )
}

export default StrategicImperativeTable
